import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { usePageMeta } from "@/hooks/usePageMeta";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { getTruckRoute } from "@/lib/mapbox";
import { getLocalTodayYMD } from "@/lib/dateOnly";
import { generateLoadNumber } from "@/lib/generateLoadNumber";
import type { PlaceData } from "@/components/ui/place-autocomplete";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { BoostOption } from "@/components/post-load/ContactPublishCard";
import { useSpotRate } from "@/hooks/useSpotRate";
import { useVerificationWarning } from "@/hooks/useVerificationWarning";
import { VerificationWarningModal } from "@/components/verification/VerificationWarningModal";

import PostLoadHeader from "@/components/post-load/PostLoadHeader";
import AIMarketStatusBar from "@/components/post-load/AIMarketStatusBar";
import ShipmentDetailsCard from "@/components/post-load/ShipmentDetailsCard";
import EquipmentDetailsCard from "@/components/post-load/EquipmentDetailsCard";
import ContactInformationCard from "@/components/post-load/ContactInformationCard";
import { LoadBriefCard, type LoadBriefValue } from "@/components/post-load/LoadBriefCard";

import AtlasRateCard from "@/components/post-load/AtlasRateCard";
import ContactPublishCard from "@/components/post-load/ContactPublishCard";
import { StopCard, type Stop } from "@/components/post-load/StopCard";

export interface InlineFormData {
  origin: string;
  originPlaceData?: PlaceData;
  destination: string;
  destinationPlaceData?: PlaceData;
  pickupEarliest: string;
  deliveryDate: string;
  pickHrs: string;
  dropHrs: string;
  loadType: "FTL" | "LTL" | "contract";
  equipmentType: string;
  length: string;
  weight: string;
  commodity: string;
  referenceNumber: string;
  comments: string;
  rateAmount: string;
  auctionMode: boolean;
  contactOptions: { primaryPhone: boolean; secondaryPhone: boolean; email: boolean };
  totalMiles: number | null;
}

const defaultForm = (): InlineFormData => ({
  origin: "",
  destination: "",
  pickupEarliest: getLocalTodayYMD(),
  deliveryDate: "",
  pickHrs: "",
  dropHrs: "",
  loadType: "FTL",
  equipmentType: "",
  length: "",
  weight: "",
  commodity: "",
  referenceNumber: "",
  comments: "",
  rateAmount: "",
  auctionMode: false,
  contactOptions: { primaryPhone: true, secondaryPhone: false, email: false },
  totalMiles: null,
});

const createDefaultStops = (): Stop[] => [
  {
    id: crypto.randomUUID(),
    stop_number: 1,
    stop_type: 'pickup',
    location: '',
    name: '',
    address: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'US',
    latitude: null,
    longitude: null,
    scheduled_date: getLocalTodayYMD(),
    pickup_latest_date: '',
    pickup_hours: '',
    dropoff_hours: '',
  },
  {
    id: crypto.randomUUID(),
    stop_number: 2,
    stop_type: 'delivery',
    location: '',
    name: '',
    address: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'US',
    latitude: null,
    longitude: null,
    scheduled_date: '',
  },
];

export default function PostLoadPageV2() {
  const { showWarningOrRun, modalProps } = useVerificationWarning();
  useEffect(() => { showWarningOrRun(() => {}); }, [showWarningOrRun]);
  const [searchParams] = useSearchParams();
  const editingLoadId = searchParams.get("edit");
  const repostLoadId = searchParams.get("repost");
  const duplicateLoadId = searchParams.get("duplicate");
  const prefillLoadId = editingLoadId || repostLoadId || duplicateLoadId;
  const mode: "edit" | "repost" | "duplicate" | "create" = editingLoadId ? "edit" : repostLoadId ? "repost" : duplicateLoadId ? "duplicate" : "create";
  const isEditing = mode === "edit";

  const pageTitle = mode === "edit" ? "Edit Load" : mode === "repost" ? "Repost Load" : mode === "duplicate" ? "Duplicate Load" : "Quick Post";

  usePageMeta({
    title: `${pageTitle} | LaneRunner`,
    description: isEditing ? "Edit an existing freight load" : "Quickly post a new freight load",
  });

  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  const [form, setForm] = useState<InlineFormData>(defaultForm);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [editDataLoaded, setEditDataLoaded] = useState(false);
  const [saveAsTemplate, setSaveAsTemplate] = useState(false);
  const [boostOption, setBoostOption] = useState<BoostOption | null>(null);
  // Optional Load Brief (recorded audio)
  const [brief, setBrief] = useState<LoadBriefValue | null>(null);
  // Multi-stop state
  const [stops, setStops] = useState<Stop[]>(createDefaultStops);
  const [expandedStopId, setExpandedStopId] = useState<string | null>(null);

  // User profile for contacts
  const [userProfile, setUserProfile] = useState({ phone: "", alternate_phone: "", email: "", first_name: "", last_name: "" });

  // Load user profile
  useEffect(() => {
    if (!user) return;
    supabase
      .from("profiles")
      .select("phone, alternate_phone, email, first_name, last_name")
      .eq("id", user.id)
      .single()
      .then(({ data }) => {
        if (data) setUserProfile({
          phone: data.phone || "",
          alternate_phone: data.alternate_phone || "",
          email: data.email || "",
          first_name: data.first_name || "",
          last_name: data.last_name || "",
        });
      });
  }, [user]);

  // ── Sync origin/destination from stops ──
  // First pickup → form origin; last delivery → form destination
  const syncFormFromStops = useCallback((currentStops: Stop[]) => {
    const firstPickup = currentStops.find(s => s.stop_type === 'pickup');
    const lastDelivery = [...currentStops].reverse().find(s => s.stop_type === 'delivery');

    setForm(prev => {
      const updates: Partial<InlineFormData> = {};
      if (firstPickup && firstPickup.city && firstPickup.state) {
        updates.origin = `${firstPickup.city}, ${firstPickup.state}`;
        updates.originPlaceData = {
          city: firstPickup.city,
          state: firstPickup.state,
          latitude: firstPickup.latitude ?? 0,
          longitude: firstPickup.longitude ?? 0,
          address: firstPickup.address,
          name: firstPickup.name,
          country: firstPickup.country || 'US',
          postal_code: firstPickup.postal_code || '',
        };
        if (firstPickup.scheduled_date) updates.pickupEarliest = firstPickup.scheduled_date;
      }
      if (lastDelivery && lastDelivery.city && lastDelivery.state) {
        updates.destination = `${lastDelivery.city}, ${lastDelivery.state}`;
        updates.destinationPlaceData = {
          city: lastDelivery.city,
          state: lastDelivery.state,
          latitude: lastDelivery.latitude ?? 0,
          longitude: lastDelivery.longitude ?? 0,
          address: lastDelivery.address,
          name: lastDelivery.name,
          country: lastDelivery.country || 'US',
          postal_code: lastDelivery.postal_code || '',
        };
        if (lastDelivery.scheduled_date) updates.deliveryDate = lastDelivery.scheduled_date;
      }
      return { ...prev, ...updates };
    });
  }, []);

  // ── Stop management handlers ──
  const handleAddStop = useCallback((type: 'pickup' | 'delivery') => {
    const newId = crypto.randomUUID();
    setStops(prev => {
      const pickups = prev.filter(s => s.stop_type === 'pickup').length;
      const deliveries = prev.filter(s => s.stop_type === 'delivery').length;
      if (type === 'pickup' && pickups >= 5) return prev;
      if (type === 'delivery' && deliveries >= 5) return prev;
      if (prev.length >= 10) return prev;

      const newStop: Stop = {
        id: newId,
        stop_number: 0,
        stop_type: type,
        location: '',
        name: '',
        address: '',
        city: '',
        state: '',
        postal_code: '',
        country: 'US',
        latitude: null,
        longitude: null,
        scheduled_date: type === 'pickup' ? getLocalTodayYMD() : '',
      };

      // Insert pickups before the first delivery (so original origin stays first).
      // Insert drops before the LAST delivery (so original final destination stays last).
      let newStops: Stop[];
      if (type === 'pickup') {
        const firstDeliveryIdx = prev.findIndex(s => s.stop_type === 'delivery');
        newStops = firstDeliveryIdx === -1
          ? [...prev, newStop]
          : [...prev.slice(0, firstDeliveryIdx), newStop, ...prev.slice(firstDeliveryIdx)];
      } else {
        let lastDeliveryIdx = -1;
        for (let i = prev.length - 1; i >= 0; i--) {
          if (prev[i].stop_type === 'delivery') { lastDeliveryIdx = i; break; }
        }
        newStops = lastDeliveryIdx === -1
          ? [...prev, newStop]
          : [...prev.slice(0, lastDeliveryIdx), newStop, ...prev.slice(lastDeliveryIdx)];
      }

      // Renumber
      return newStops.map((s, i) => ({ ...s, stop_number: i + 1 }));
    });
    // Auto-expand the new stop
    setExpandedStopId(newId);
  }, []);

  const handleDeleteStop = useCallback((index: number) => {
    setStops(prev => {
      if (prev.length <= 2) return prev;
      const updated = prev.filter((_, i) => i !== index);
      return updated.map((s, i) => ({ ...s, stop_number: i + 1 }));
    });
  }, []);

  const handleStopUpdate = useCallback((index: number, updatedStop: Stop) => {
    setStops(prev => {
      const updated = [...prev];
      updated[index] = updatedStop;
      // Enforce pickups-before-deliveries order
      const pickups = updated.filter(s => s.stop_type === 'pickup');
      const deliveries = updated.filter(s => s.stop_type === 'delivery');
      const sorted = [...pickups, ...deliveries];
      return sorted.map((s, i) => ({ ...s, stop_number: i + 1 }));
    });
  }, []);

  // Sync form from stops whenever stops change
  useEffect(() => {
    syncFormFromStops(stops);
  }, [stops, syncFormFromStops]);

  // Fetch existing load for edit / repost / duplicate
  useEffect(() => {
    if (!prefillLoadId || editDataLoaded) return;

    const fetchLoad = async () => {
      const { data, error } = await supabase
        .from("loads")
        .select("*")
        .eq("id", prefillLoadId)
        .single();

      if (error || !data) {
        toast({ variant: "destructive", title: "Load not found", description: "Could not load the requested record." });
        navigate("/my-loads");
        return;
      }

      // Fetch load_stops
      const { data: loadStops } = await supabase
        .from("load_stops")
        .select("*")
        .eq("load_id", prefillLoadId)
        .order("stop_number", { ascending: true });

      // Parse pickHrs/dropHrs from special_instructions
      let comments = data.special_instructions || "";
      let pickHrs = "";
      let dropHrs = "";
      const parts = comments.split(" | ");
      const filtered: string[] = [];
      for (const part of parts) {
        if (part.startsWith("Pick Hrs: ")) {
          pickHrs = part.replace("Pick Hrs: ", "");
        } else if (part.startsWith("Drop Hrs: ")) {
          dropHrs = part.replace("Drop Hrs: ", "");
        } else {
          filtered.push(part);
        }
      }
      comments = filtered.join(" | ");

      const originPlaceData: PlaceData = {
        city: data.origin_city,
        state: data.origin_state,
        latitude: data.origin_latitude ? Number(data.origin_latitude) : 0,
        longitude: data.origin_longitude ? Number(data.origin_longitude) : 0,
        address: data.origin_address,
        name: data.origin_name,
        country: data.origin_country || "US",
        postal_code: data.origin_postal_code || "",
      };

      const destinationPlaceData: PlaceData = {
        city: data.destination_city,
        state: data.destination_state,
        latitude: data.destination_latitude ? Number(data.destination_latitude) : 0,
        longitude: data.destination_longitude ? Number(data.destination_longitude) : 0,
        address: data.destination_address,
        name: data.destination_name,
        country: data.destination_country || "US",
        postal_code: data.destination_postal_code || "",
      };

      setForm({
        origin: `${data.origin_city}, ${data.origin_state}`,
        originPlaceData,
        destination: `${data.destination_city}, ${data.destination_state}`,
        destinationPlaceData,
        pickupEarliest: mode === "repost" ? getLocalTodayYMD() : data.pickup_date,
        deliveryDate: mode === "repost" ? "" : (data.delivery_date || ""),
        pickHrs,
        dropHrs,
        loadType: (data.load_type as "FTL" | "LTL" | "contract") || "FTL",
        equipmentType: data.equipment_type || "",
        length: data.length_ft ? String(data.length_ft) : "",
        weight: data.weight_lbs ? String(data.weight_lbs) : "",
        commodity: data.commodity || "",
        referenceNumber: data.reference_number || "",
        comments,
        rateAmount: mode === "repost" ? "" : (data.rate_amount ? String(data.rate_amount) : ""),
        auctionMode: false,
        contactOptions: {
          primaryPhone: !!data.contact_phone,
          secondaryPhone: !!data.contact_phone_secondary,
          email: !!data.contact_email,
        },
        totalMiles: data.total_miles || null,
      });

      // Hydrate stops from load_stops or synthesize from load data
      if (loadStops && loadStops.length > 0) {
        const hydratedStops: Stop[] = loadStops.map((ls) => ({
          id: ls.id,
          stop_number: ls.stop_number,
          stop_type: ls.stop_type as 'pickup' | 'delivery',
          location: `${ls.city}, ${ls.state}`,
          name: ls.name,
          address: ls.address,
          city: ls.city,
          state: ls.state,
          postal_code: ls.postal_code || '',
          country: ls.country || 'US',
          latitude: ls.latitude ? Number(ls.latitude) : null,
          longitude: ls.longitude ? Number(ls.longitude) : null,
          scheduled_date: mode === "repost"
            ? (ls.stop_type === 'pickup' ? getLocalTodayYMD() : '')
            : (ls.scheduled_date || ''),
          scheduled_time_start: ls.scheduled_time_start || undefined,
          scheduled_time_end: ls.scheduled_time_end || undefined,
          contact_name: ls.contact_name || undefined,
          contact_phone: ls.contact_phone || undefined,
          contact_email: ls.contact_email || undefined,
          instructions: ls.instructions || undefined,
        }));
        setStops(hydratedStops);
      } else {
        // Synthesize from load origin/destination
        setStops([
          {
            id: crypto.randomUUID(),
            stop_number: 1,
            stop_type: 'pickup',
            location: `${data.origin_city}, ${data.origin_state}`,
            name: data.origin_name,
            address: data.origin_address,
            city: data.origin_city,
            state: data.origin_state,
            postal_code: data.origin_postal_code || '',
            country: data.origin_country || 'US',
            latitude: data.origin_latitude ? Number(data.origin_latitude) : null,
            longitude: data.origin_longitude ? Number(data.origin_longitude) : null,
            scheduled_date: mode === "repost" ? getLocalTodayYMD() : data.pickup_date,
            pickup_hours: pickHrs,
          },
          {
            id: crypto.randomUUID(),
            stop_number: 2,
            stop_type: 'delivery',
            location: `${data.destination_city}, ${data.destination_state}`,
            name: data.destination_name,
            address: data.destination_address,
            city: data.destination_city,
            state: data.destination_state,
            postal_code: data.destination_postal_code || '',
            country: data.destination_country || 'US',
            latitude: data.destination_latitude ? Number(data.destination_latitude) : null,
            longitude: data.destination_longitude ? Number(data.destination_longitude) : null,
            scheduled_date: mode === "repost" ? "" : (data.delivery_date || ""),
            dropoff_hours: dropHrs,
          },
        ]);
      }

      setEditDataLoaded(true);
    };

    fetchLoad();
  }, [prefillLoadId, editDataLoaded, navigate, toast, mode]);

  // Recalculate distance when origin/dest change
  useEffect(() => {
    const calcDistance = async () => {
      const o = form.originPlaceData;
      const d = form.destinationPlaceData;
      if (!o || !d) return;
      try {
        const route = await getTruckRoute([o.longitude, o.latitude], [d.longitude, d.latitude]);
        if (route) setForm(prev => ({ ...prev, totalMiles: route.distance }));
      } catch { /* ignore */ }
    };
    calcDistance();
  }, [form.originPlaceData, form.destinationPlaceData]);

  const updateField = useCallback(<K extends keyof InlineFormData>(field: K, value: InlineFormData[K]) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setErrors(prev => {
      if (!(field in prev)) return prev;
      const next = { ...prev };
      delete next[field as string];
      return next;
    });
  }, []);

  // ── Persist load_stops helper ──
  const persistStops = async (loadId: string, stopsToSave: Stop[]) => {
    // Delete existing stops first (for edit/repost)
    await supabase.from("load_stops").delete().eq("load_id", loadId);

    if (stopsToSave.length === 0) return;

    const stopsPayload = stopsToSave.map(stop => ({
      load_id: loadId,
      stop_number: stop.stop_number,
      stop_type: stop.stop_type,
      name: stop.name || stop.location || stop.city || 'Stop',
      address: stop.address || stop.location || `${stop.city}, ${stop.state}`,
      city: stop.city,
      state: stop.state,
      postal_code: stop.postal_code || null,
      country: stop.country || 'US',
      latitude: stop.latitude || null,
      longitude: stop.longitude || null,
      scheduled_date: stop.scheduled_date || (stop.stop_type === 'pickup' ? getLocalTodayYMD() : null),
      scheduled_time_start: stop.scheduled_time_start || null,
      scheduled_time_end: stop.scheduled_time_end || null,
      contact_name: stop.contact_name || null,
      contact_phone: stop.contact_phone || null,
      contact_email: stop.contact_email || null,
      instructions: stop.instructions || null,
    }));

    const { error } = await supabase.from("load_stops").insert(stopsPayload);
    if (error) console.error("Error saving load_stops:", error);
  };

  const handleSubmit = async () => {
    // Validate required fields
    const newErrors: Record<string, string> = {};
    if (!form.originPlaceData) newErrors.origin = "Origin is required";
    if (!form.destinationPlaceData) newErrors.destination = "Destination is required";
    if (!form.pickupEarliest) newErrors.pickupEarliest = "Pickup date is required";
    if (!form.equipmentType) newErrors.equipmentType = "Equipment type is required";
    const lengthVal = parseFloat(form.length);
    if (!form.length || isNaN(lengthVal) || lengthVal <= 0) newErrors.length = "Length is required";
    const weightNum = parseInt(form.weight.replace(/\D/g, ""), 10);
    if (!form.weight || isNaN(weightNum) || weightNum <= 0) newErrors.weight = "Weight is required";

    // Validate first pickup has location
    const firstPickup = stops.find(s => s.stop_type === 'pickup');
    if (!firstPickup?.city) newErrors.origin = "Pickup location is required";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setErrors({});

    const rateNum = parseFloat(form.rateAmount.replace(/,/g, "")) || 0;
    const hasContact = form.contactOptions.primaryPhone || form.contactOptions.secondaryPhone || form.contactOptions.email;
    if (!hasContact) return toast({ variant: "destructive", title: "Select at least one contact method" });

    setIsSubmitting(true);
    try {
      if (!user) throw new Error("Not authenticated");

      const { data: userCompanies } = await supabase.rpc("get_user_companies", { user_uuid: user.id });
      const companyId = userCompanies?.[0] || null;

      // Derive origin/dest from stops
      const firstPickupStop = stops.find(s => s.stop_type === 'pickup')!;
      const lastDeliveryStop = [...stops].reverse().find(s => s.stop_type === 'delivery')!;

      const o = form.originPlaceData!;
      const d = form.destinationPlaceData!;
      const lengthNum = parseFloat(form.length) || null;

      const contactPhone = form.contactOptions.primaryPhone ? userProfile.phone : null;
      const contactPhoneSecondary = form.contactOptions.secondaryPhone ? userProfile.alternate_phone : null;
      const contactEmail = form.contactOptions.email ? userProfile.email : null;
      const contactName = [userProfile.first_name, userProfile.last_name].filter(Boolean).join(" ") || userProfile.email?.split("@")[0] || "";

      const payload = {
        origin_name: o.name || o.city,
        origin_address: o.address || `${o.city}, ${o.state}`,
        origin_city: o.city,
        origin_state: o.state,
        origin_country: o.country || "US",
        origin_postal_code: o.postal_code || null,
        origin_latitude: o.latitude,
        origin_longitude: o.longitude,
        destination_name: d.name || d.city,
        destination_address: d.address || `${d.city}, ${d.state}`,
        destination_city: d.city,
        destination_state: d.state,
        destination_country: d.country || "US",
        destination_postal_code: d.postal_code || null,
        destination_latitude: d.latitude,
        destination_longitude: d.longitude,
        pickup_date: firstPickupStop.scheduled_date || form.pickupEarliest || getLocalTodayYMD(),
        delivery_date: lastDeliveryStop?.scheduled_date || form.deliveryDate || null,
        equipment_type: form.equipmentType as any,
        weight_lbs: weightNum,
        length_ft: lengthNum,
        load_type: form.loadType,
        rate_amount: rateNum || null,
        rate_type: "spot",
        rate_currency: "USD",
        contact_name: contactName,
        contact_email: contactEmail,
        contact_phone: contactPhone,
        contact_phone_secondary: contactPhoneSecondary,
        special_instructions: [form.comments, form.pickHrs && `Pick Hrs: ${form.pickHrs}`, form.dropHrs && `Drop Hrs: ${form.dropHrs}`].filter(Boolean).join(" | ") || null,
        commodity: form.commodity || null,
        reference_number: form.referenceNumber.trim() || null,
        total_miles: form.totalMiles || null,
        company_id: companyId,
      };

      if (isEditing && editingLoadId) {
        // UPDATE existing load
        const { error } = await supabase
          .from("loads")
          .update(payload)
          .eq("id", editingLoadId);

        if (error) throw error;

        // Persist stops
        await persistStops(editingLoadId, stops);

        toast({ title: "Load updated", description: "Your changes have been saved." });
      } else {
        // INSERT new load
        const { data: newLoad, error } = await supabase
          .from("loads")
          .insert({
            ...payload,
            created_by: user.id,
            status: "posted",
            load_number: form.referenceNumber.trim() || generateLoadNumber(),
          })
          .select("id")
          .single();

        if (error) throw error;

        // Persist stops
        await persistStops(newLoad.id, stops);

        // Optional: upload Load Brief audio (best-effort, never blocks publish)
        if (brief) {
          try {
            const ext = brief.mime.includes("mp4") ? "mp4" : "webm";
            const path = `${user.id}/${newLoad.id}.${ext}`;
            const { error: upErr } = await supabase.storage
              .from("load-briefs")
              .upload(path, brief.blob, { contentType: brief.mime, upsert: true });
            if (upErr) {
              toast({
                title: "Load posted",
                description: "Brief couldn't be saved — load is live without it.",
              });
            } else {
              await supabase
                .from("loads")
                .update({
                  brief_audio_path: path,
                  brief_mime_type: brief.mime,
                  brief_duration_seconds: brief.durationSec,
                } as any)
                .eq("id", newLoad.id);
            }
          } catch (briefErr) {
            console.error("Load brief upload failed:", briefErr);
          }
        }

        supabase.functions.invoke("check-rate-alerts", { body: { load_id: newLoad.id } }).catch(console.error);

        // Boost if selected
        if (boostOption) {
          try {
            const { data: boostResult } = await supabase.rpc('boost_load' as any, {
              p_load_id: newLoad.id,
              p_duration_minutes: boostOption.duration,
              p_credits: boostOption.credits,
            });
            const res = boostResult as any;
            if (res?.success) {
              toast({ title: "Load boosted!", description: `Your load is ${res.status === 'active' ? 'now featured' : 'queued for boosting'}.` });
            } else {
              toast({ variant: "destructive", title: "Boost failed", description: res?.error || "Could not boost load. Load was still posted." });
            }
          } catch (boostErr) {
            console.error("Boost error:", boostErr);
            toast({ variant: "destructive", title: "Boost failed", description: "Load posted successfully but boost failed." });
          }
        }

        // Save as template if checked
        if (saveAsTemplate) {
          const o = form.originPlaceData!;
          const d = form.destinationPlaceData!;
          supabase.from("load_templates").insert({
            created_by: user.id,
            name: `${o.city}, ${o.state} → ${d.city}, ${d.state} (${form.equipmentType})`,
            origin_city: o.city,
            origin_state: o.state,
            origin_latitude: o.latitude,
            origin_longitude: o.longitude,
            origin_address: o.address || `${o.city}, ${o.state}`,
            origin_name: o.name || o.city,
            origin_country: o.country || "US",
            origin_postal_code: o.postal_code || null,
            destination_city: d.city,
            destination_state: d.state,
            destination_latitude: d.latitude,
            destination_longitude: d.longitude,
            destination_address: d.address || `${d.city}, ${d.state}`,
            destination_name: d.name || d.city,
            destination_country: d.country || "US",
            destination_postal_code: d.postal_code || null,
            equipment_type: form.equipmentType,
            weight_lbs: weightNum,
            length_ft: parseFloat(form.length) || null,
            commodity: form.commodity || null,
            load_type: form.loadType,
            comments: form.comments || null,
            pick_hrs: form.pickHrs || null,
            drop_hrs: form.dropHrs || null,
          } as any).then(({ error: tplErr }) => {
            if (tplErr) console.error("Template save error:", tplErr);
          });
        }

        toast({ title: "Load posted", description: "Your load has been successfully posted." });
      }

      navigate("/my-loads");
    } catch (error: any) {
      const msg = error?.message || (typeof error === 'string' ? error : JSON.stringify(error));
      console.error("Error saving load:", msg, error);
      toast({ variant: "destructive", title: isEditing ? "Error updating load" : "Error posting load", description: msg || "An unexpected error occurred" });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Stop count helpers for UI
  const pickupCount = stops.filter(s => s.stop_type === 'pickup').length;
  const deliveryCount = stops.filter(s => s.stop_type === 'delivery').length;

  // Spot rate for preflight checks
  const { data: spotRate } = useSpotRate({
    originLat: form.originPlaceData?.latitude,
    originLon: form.originPlaceData?.longitude,
    destLat: form.destinationPlaceData?.latitude,
    destLon: form.destinationPlaceData?.longitude,
    laneMiles: form.totalMiles,
    equipmentType: form.equipmentType,
  });

  // isDirty — true when any field differs from defaults
  const isDirty = !!(
    form.origin || form.destination || form.equipmentType || form.weight ||
    form.length || form.commodity || form.referenceNumber || form.comments ||
    form.rateAmount
  );


  return (
    <div className="min-h-screen flex flex-col bg-background">
      <PostLoadHeader isEditing={isEditing} title={pageTitle} isDirty={isDirty} />
      <AIMarketStatusBar
        originLat={form.originPlaceData?.latitude}
        originLon={form.originPlaceData?.longitude}
        destLat={form.destinationPlaceData?.latitude}
        destLon={form.destinationPlaceData?.longitude}
        laneMiles={form.totalMiles}
        equipmentType={form.equipmentType}
      />

      {/* Three-column layout */}
      <div className="flex-1 w-full min-w-0 max-w-full px-3 md:px-4 py-4 grid gap-4 items-start grid-cols-1 md:[grid-template-columns:1fr_1fr_340px]">
        {/* LEFT COLUMN */}
        <div className="space-y-3">
          <ShipmentDetailsCard
            origin={form.origin}
            destination={form.destination}
            pickupEarliest={form.pickupEarliest}
            deliveryDate={form.deliveryDate}
            pickHrs={form.pickHrs}
            dropHrs={form.dropHrs}
            originCity={form.originPlaceData?.city}
            originState={form.originPlaceData?.state}
            destinationCity={form.destinationPlaceData?.city}
            destinationState={form.destinationPlaceData?.state}
            onOriginChange={(val, pd) => {
              updateField("origin", val);
              if (pd) updateField("originPlaceData", pd);
              if (pd) {
                setErrors(prev => { const n = {...prev}; delete n.origin; return n; });
                // Sync to first pickup stop
                setStops(prev => {
                  const idx = prev.findIndex(s => s.stop_type === 'pickup');
                  if (idx === -1) return prev;
                  const updated = [...prev];
                  updated[idx] = {
                    ...updated[idx],
                    location: val,
                    city: pd.city,
                    state: pd.state,
                    address: pd.address || `${pd.city}, ${pd.state}`,
                    name: pd.name || pd.city,
                    country: pd.country || 'US',
                    postal_code: pd.postal_code || '',
                    latitude: pd.latitude,
                    longitude: pd.longitude,
                  };
                  return updated;
                });
              }
            }}
            onDestinationChange={(val, pd) => {
              updateField("destination", val);
              if (pd) updateField("destinationPlaceData", pd);
              if (pd) {
                setErrors(prev => { const n = {...prev}; delete n.destination; return n; });
                // Sync to last delivery stop
                setStops(prev => {
                  const lastDelIdx = prev.map((s, i) => s.stop_type === 'delivery' ? i : -1).filter(i => i !== -1).pop();
                  if (lastDelIdx === undefined) return prev;
                  const updated = [...prev];
                  updated[lastDelIdx] = {
                    ...updated[lastDelIdx],
                    location: val,
                    city: pd.city,
                    state: pd.state,
                    address: pd.address || `${pd.city}, ${pd.state}`,
                    name: pd.name || pd.city,
                    country: pd.country || 'US',
                    postal_code: pd.postal_code || '',
                    latitude: pd.latitude,
                    longitude: pd.longitude,
                  };
                  return updated;
                });
              }
            }}
            onPickupEarliestChange={v => updateField("pickupEarliest", v)}
            onDeliveryDateChange={v => updateField("deliveryDate", v)}
            onPickHrsChange={v => updateField("pickHrs", v)}
            onDropHrsChange={v => updateField("dropHrs", v)}
            errors={errors}
          />
          <EquipmentDetailsCard
            loadType={form.loadType}
            equipmentType={form.equipmentType}
            length={form.length}
            weight={form.weight}
            commodity={form.commodity}
            referenceNumber={form.referenceNumber}
            comments={form.comments}
            onLoadTypeChange={v => updateField("loadType", v as any)}
            onEquipmentTypeChange={v => updateField("equipmentType", v)}
            onLengthChange={v => updateField("length", v)}
            onWeightChange={v => updateField("weight", v)}
            onCommodityChange={v => updateField("commodity", v)}
            onReferenceNumberChange={v => updateField("referenceNumber", v)}
            onCommentsChange={v => updateField("comments", v)}
            totalMiles={form.totalMiles}
            errors={errors}
          />
          <LoadBriefCard value={brief} onChange={setBrief} />
          <ContactInformationCard
            userProfile={userProfile}
            contactOptions={form.contactOptions}
            onContactOptionsChange={v => updateField("contactOptions", v)}
          />
        </div>

        {/* MIDDLE COLUMN — Stops */}
        <div className="space-y-3">
          <div className="rounded-lg border border-border bg-card p-3 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-foreground">
                Pickups & Drops
                <span className="ml-2 text-xs font-normal text-muted-foreground">
                  {pickupCount}p{deliveryCount}d
                </span>
              </h3>
              <span className="text-[10px] text-muted-foreground">
                {stops.length}/10 stops
              </span>
            </div>

            {stops.map((stop, index) => (
              <StopCard
                key={stop.id}
                stop={stop}
                index={index}
                canDelete={stops.length > 2}
                isExpanded={expandedStopId === stop.id}
                onToggleExpand={() => setExpandedStopId(prev => prev === stop.id ? null : stop.id)}
                onUpdate={(updated) => handleStopUpdate(index, updated)}
                onDelete={() => handleDeleteStop(index)}
              />
            ))}

            <div className="grid grid-cols-2 gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => handleAddStop('pickup')}
                disabled={pickupCount >= 5 || stops.length >= 10}
                className="text-xs"
              >
                <Plus className="h-3.5 w-3.5 mr-1" />
                Add Pickup
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => handleAddStop('delivery')}
                disabled={deliveryCount >= 5 || stops.length >= 10}
                className="text-xs"
              >
                <Plus className="h-3.5 w-3.5 mr-1" />
                Add Drop
              </Button>
            </div>
          </div>

        </div>

        {/* RIGHT COLUMN */}
        <div className="space-y-3">
          <AtlasRateCard
            originLat={stops[0]?.latitude}
            originLon={stops[0]?.longitude}
            destLat={stops[stops.length - 1]?.latitude}
            destLon={stops[stops.length - 1]?.longitude}
            laneMiles={form.totalMiles}
            equipmentType={form.equipmentType}
          />
          <ContactPublishCard
            rateAmount={form.rateAmount}
            auctionMode={form.auctionMode}
            onRateChange={v => updateField("rateAmount", v)}
            onAuctionModeChange={v => updateField("auctionMode", v)}
            onSubmit={handleSubmit}
            isSubmitting={isSubmitting}
            totalMiles={form.totalMiles}
            isEditing={isEditing}
            saveAsTemplate={saveAsTemplate}
            onSaveAsTemplateChange={setSaveAsTemplate}
            boostOption={boostOption}
            onBoostOptionChange={setBoostOption}
            spotRate={spotRate}
          />
        </div>
      </div>
      <VerificationWarningModal {...modalProps} />
    </div>
  );
}
